"use client";

import { c as _c } from "react/compiler-runtime";
import { GroupedOptionLabelValue, OptionLabelValue } from "@/types/input";
import { cn } from "@/utils/cn";
import { ChevronDown, X } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { Button, Label, ListBox, ListBoxItem, MenuTrigger, Popover, Text } from "react-aria-components";
type SelectInputProps = {
  label?: string;
  description?: string;
  errorMessage?: string;
  options: (GroupedOptionLabelValue | OptionLabelValue)[];
  placeholder?: string;
  className?: string;
  multiple?: boolean;
  clearable?: boolean;
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  variant?: "outline" | "underline";
  maxVisibleTags?: number;
};
function SelectInput(t0) {
  const $ = _c(58);
  const {
    label,
    description,
    errorMessage,
    options,
    placeholder: t1,
    className,
    multiple: t2,
    clearable: t3,
    value,
    onChange,
    disabled: t4,
    variant: t5,
    maxVisibleTags: t6
  } = t0;
  const placeholder = t1 === undefined ? "Select an option" : t1;
  const multiple = t2 === undefined ? false : t2;
  const clearable = t3 === undefined ? false : t3;
  const disabled = t4 === undefined ? false : t4;
  const variant = t5 === undefined ? "outline" : t5;
  const maxVisibleTags = t6 === undefined ? 1 : t6;
  let t7;
  if ($[0] !== value) {
    t7 = value || [];
    $[0] = value;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const [selectedValues, setSelectedValues] = useState(t7);
  const isGrouped = options.length > 0 && "options" in options[0];
  let t8;
  bb0: {
    if (isGrouped) {
      const t9 = options as GroupedOptionLabelValue[];
      let t10;
      if ($[2] !== t9) {
        t10 = t9.flatMap(_temp);
        $[2] = t9;
        $[3] = t10;
      } else {
        t10 = $[3];
      }
      t8 = t10;
      break bb0;
    }
    t8 = options as OptionLabelValue[];
  }
  const flatOptions = t8;
  let t9;
  if ($[4] !== multiple || $[5] !== onChange || $[6] !== selectedValues) {
    t9 = optionValue => {
      const isAlreadySelected = selectedValues.includes(optionValue);
      let newArray;
      if (isAlreadySelected) {
        newArray = selectedValues.filter(v => v !== optionValue);
      } else {
        newArray = multiple ? [...selectedValues, optionValue] : [optionValue];
      }
      setSelectedValues(newArray);
      onChange?.(newArray);
    };
    $[4] = multiple;
    $[5] = onChange;
    $[6] = selectedValues;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const handleSelect = t9;
  let t10;
  if ($[8] !== onChange) {
    t10 = e => {
      e.stopPropagation();
      setSelectedValues([]);
      onChange?.([]);
    };
    $[8] = onChange;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const handleClear = t10;
  let t11;
  bb1: {
    if (selectedValues.length <= maxVisibleTags) {
      t11 = selectedValues;
      break bb1;
    }
    let t12;
    if ($[10] !== maxVisibleTags || $[11] !== selectedValues) {
      t12 = selectedValues.slice(0, maxVisibleTags);
      $[10] = maxVisibleTags;
      $[11] = selectedValues;
      $[12] = t12;
    } else {
      t12 = $[12];
    }
    t11 = t12;
  }
  const displayedValues = t11;
  const extraCount = selectedValues.length > maxVisibleTags ? selectedValues.length - maxVisibleTags : 0;
  let t12;
  if ($[13] !== handleSelect || $[14] !== isGrouped || $[15] !== options || $[16] !== selectedValues) {
    t12 = () => {
      if (isGrouped) {
        const items = [];
        (options as GroupedOptionLabelValue[]).forEach(group_0 => {
          items.push(<ListBoxItem key={`header-${group_0.value}`} id={`header-${group_0.value}`} className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-200 mb-1 cursor-default" isDisabled={true}>{group_0.label}</ListBoxItem>);
          group_0.options.forEach(opt => {
            const isSelected = selectedValues.includes(opt.value);
            items.push(<ListBoxItem key={`${group_0.value}-${opt.value}`} id={opt.value} onAction={() => handleSelect(opt.value)} className={cn("cursor-pointer rounded px-2 py-2 text-body flex items-center justify-between ml-2", "hover:bg-gray-100 focus:bg-gray-100 outline-none", isSelected && "bg-primary-100 text-primary-700")}>{opt.label}{isSelected && <span className="pl-1 text-primary-600">✔</span>}</ListBoxItem>);
          });
        });
        return items;
      } else {
        return (options as OptionLabelValue[]).map(opt_0 => {
          const isSelected_0 = selectedValues.includes(opt_0.value);
          return <ListBoxItem key={opt_0.value} id={opt_0.value} onAction={() => handleSelect(opt_0.value)} className={cn("cursor-pointer rounded px-2 py-2 text-body flex items-center justify-between", "hover:bg-gray-100 focus:bg-gray-100 outline-none", isSelected_0 && "bg-primary-100 text-primary-700")}>{opt_0.label}{isSelected_0 && <span className="pl-1 text-primary-600">✔</span>}</ListBoxItem>;
        });
      }
    };
    $[13] = handleSelect;
    $[14] = isGrouped;
    $[15] = options;
    $[16] = selectedValues;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const renderOptions = t12;
  let t13;
  if ($[18] !== className) {
    t13 = cn("flex flex-col gap-2 py-3", className);
    $[18] = className;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== label) {
    t14 = label && <Label className="text-title-medium">{label}</Label>;
    $[20] = label;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = errorMessage ? "border-red-500" : "border-gray-300";
  const t16 = variant === "outline" ? "border rounded-lg focus:border-primary-600 focus:ring-2 focus:ring-primary-100" : "border-0 border-b focus:border-b-1 focus:border-primary-600";
  let t17;
  if ($[22] !== t15 || $[23] !== t16) {
    t17 = cn("flex items-center justify-between px-3 h-12 text-body w-full cursor-pointer", "disabled:opacity-50 disabled:cursor-not-allowed", t15, t16);
    $[22] = t15;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== displayedValues || $[26] !== extraCount || $[27] !== flatOptions || $[28] !== placeholder || $[29] !== selectedValues.length) {
    t18 = <div className="flex flex-wrap gap-1 truncate">{selectedValues.length > 0 ? <>{displayedValues.map(val => {
          const optLabel = flatOptions?.find(o => o.value === val)?.label || val;
          return <span key={optLabel} className="px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded-md">{optLabel}</span>;
        })}{extraCount > 0 && <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-md">+{extraCount} more</span>}</> : <span className="text-gray-400">{placeholder}</span>}</div>;
    $[25] = displayedValues;
    $[26] = extraCount;
    $[27] = flatOptions;
    $[28] = placeholder;
    $[29] = selectedValues.length;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ChevronDown size={16} aria-hidden="true" />;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== disabled || $[33] !== t17 || $[34] !== t18) {
    t20 = <Button className={t17} isDisabled={disabled}>{t18}{t19}</Button>;
    $[32] = disabled;
    $[33] = t17;
    $[34] = t18;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== clearable || $[37] !== handleClear || $[38] !== selectedValues.length) {
    t21 = clearable && selectedValues.length > 0 && <button aria-label="Clear selection" type="button" onClick={handleClear} className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={16} /></button>;
    $[36] = clearable;
    $[37] = handleClear;
    $[38] = selectedValues.length;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== isGrouped || $[41] !== options || $[42] !== renderOptions) {
    t22 = (isGrouped && (options as GroupedOptionLabelValue[]).some(_temp2) || !isGrouped && options.length > 0) && <Popover className="border border-gray-200 rounded-md shadow-lg bg-white p-1 mt-1"><ListBox className="outline-none max-h-60 overflow-y-auto scrollbar-thin">{renderOptions()}</ListBox></Popover>;
    $[40] = isGrouped;
    $[41] = options;
    $[42] = renderOptions;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t20 || $[45] !== t21 || $[46] !== t22) {
    t23 = <MenuTrigger><div className="relative w-full">{t20}{t21}{t22}</div></MenuTrigger>;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== description) {
    t24 = description && <Text slot="description" className="text-body-small text-gray-500">{description}</Text>;
    $[48] = description;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== errorMessage) {
    t25 = errorMessage && <Text slot="errorMessage" className="text-body-small-medium text-destructive">{errorMessage}</Text>;
    $[50] = errorMessage;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t13 || $[53] !== t14 || $[54] !== t23 || $[55] !== t24 || $[56] !== t25) {
    t26 = <div className={t13}>{t14}{t23}{t24}{t25}</div>;
    $[52] = t13;
    $[53] = t14;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  return t26;
}
function _temp2(group_1) {
  return group_1.options.length > 0;
}
function _temp(group) {
  return group.options;
}
export { SelectInput, type GroupedOptionLabelValue, type OptionLabelValue };